import { useEffect, useState, useCallback } from "react";
import { getLifecycle, selectCycle } from "@/api";
import Challenge from "@/components/Challenge";
import FindabilityCycleReady from "@/components/FindabilityCycleReady";
import BaselineScreen from "@/components/BaselineScreen";
import PastCycles from "@/components/PastCycles";
import { decideView } from "@/lib/cycleFlow";
import { makeLegacySource, makeCycleSource } from "@/lib/challengeSource";

// Cycle switcher labelling: focus/title (primary) + start date (secondary) + status/progress.
function cycleLabel(c) {
  const title = (c.focus_label || "").trim() || "New cycle";
  let when = "";
  if (c.started_at) {
    const d = new Date(c.started_at);
    if (!isNaN(d)) when = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  const n = c.completed_effort_count || 0;
  let state = "Active";
  if (c.status === "activating_baseline_pending") state = "Setting up";
  else if (c.status === "ready_to_complete") state = "Ready";
  const progress = `${state} ${n}/30`;
  return when ? `${title} · ${when} · ${progress}` : `${title} · ${progress}`;
}

function CycleSwitcher({ cycles, availableCount, currentId, onSelect, onStartNew, onViewHistory }) {
  return (
    <div
      data-testid="cycle-switcher"
      style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
               padding: "0.7rem 1rem", background: "#0f1115", borderBottom: "1px solid #23262d" }}
    >
      <span style={{ color: "#8b5cf6", fontWeight: 600, fontSize: 12, letterSpacing: ".04em",
                     textTransform: "uppercase" }}>Your cycles</span>
      <select
        data-testid="cycle-switcher-select"
        value={currentId || ""}
        onChange={(e) => onSelect(e.target.value)}
        style={{ background: "#16181d", color: "#f4f4f5", border: "1px solid #2a2e37",
                 borderRadius: 8, padding: "0.45rem 0.6rem", fontSize: ".9rem", maxWidth: 420 }}
      >
        {cycles.map((c) => (
          <option key={c.cycle_id} value={c.cycle_id} data-testid={`cycle-option-${c.cycle_id}`}>
            {cycleLabel(c)}
          </option>
        ))}
      </select>
      {availableCount > 0 && (
        <button
          type="button"
          data-testid="start-new-cycle-btn"
          onClick={onStartNew}
          style={{ padding: "0.45rem 0.9rem", borderRadius: 999, border: "none", cursor: "pointer",
                   background: "#8b5cf6", color: "#fff", fontWeight: 600, fontSize: ".85rem" }}
        >
          + Start your new cycle
        </button>
      )}
      <button
        type="button"
        data-testid="view-past-cycles-btn"
        onClick={onViewHistory}
        style={{ padding: "0.45rem 0.9rem", borderRadius: 999, cursor: "pointer",
                 background: "transparent", color: "#a78bfa", border: "1px solid #3a3550",
                 fontWeight: 600, fontSize: ".85rem" }}
      >
        Past cycles (read-only)
      </button>
    </div>
  );
}

// After authentication, fetch lifecycle state and branch BEFORE the legacy generator:
//  - ready  : available entitlement + no current cycle -> paid-entry surface
//  - active : a current cycle exists -> Baseline step, else the branded Challenge (cycle-backed)
//  - legacy : otherwise -> existing branded Challenge (legacy-backed), unchanged behavior
// Multi-cycle (flag ON): a switcher lets the account move between concurrent active cycles, start a
// new purchased cycle, or open historical cycles read-only. The prior cycle is never auto-closed.
export default function CycleGate({ user, setUser }) {
  const [lifecycle, setLifecycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("auto"); // "auto" | "start_new" | "history"

  const load = useCallback(async () => {
    try {
      setLifecycle(await getLifecycle());
    } catch {
      setLifecycle(null); // fail safe -> legacy generator, no paid claims shown
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="auth-loading" data-testid="cycle-gate-loading">
        <div className="gen-dots"><span /><span /><span /></div>
        Loading your Findability Cycle…
      </div>
    );
  }

  const multi = !!(lifecycle && lifecycle.multi_cycle);
  const cycles = (lifecycle && lifecycle.cycles) || [];
  const current = lifecycle && lifecycle.current_cycle;
  const currentId = current && current.cycle_id;
  const availableCount = (lifecycle && lifecycle.available_entitlement_count) || 0;

  // Historical cycles (read-only) view.
  if (mode === "history") {
    return (
      <div data-testid="cycle-history-view">
        <div style={{ padding: "0.7rem 1rem", background: "#0f1115", borderBottom: "1px solid #23262d" }}>
          <button
            type="button"
            data-testid="history-back-btn"
            onClick={() => setMode("auto")}
            style={{ padding: "0.45rem 0.9rem", borderRadius: 999, cursor: "pointer",
                     background: "transparent", color: "#a78bfa", border: "1px solid #3a3550",
                     fontWeight: 600, fontSize: ".85rem" }}
          >
            ← Back to your cycle
          </button>
        </div>
        <PastCycles />
      </div>
    );
  }

  // Explicitly starting a NEW cycle (never auto-started): the intake surface.
  const view = decideView(lifecycle);
  if (mode === "start_new" || view === "ready") {
    return (
      <FindabilityCycleReady
        count={availableCount}
        user={user}
        onActivated={(lc) => { setLifecycle(lc); setMode("auto"); }}
      />
    );
  }

  const switcher = multi && (cycles.length > 1 || availableCount > 0) ? (
    <CycleSwitcher
      cycles={cycles}
      availableCount={availableCount}
      currentId={currentId}
      onSelect={async (id) => { if (id && id !== currentId) setLifecycle(await selectCycle(id)); }}
      onStartNew={() => setMode("start_new")}
      onViewHistory={() => setMode("history")}
    />
  ) : null;

  if (view === "active") {
    const cycle = current;
    // Pre-Baseline cycle (activation fully settled) -> Day 0 Baseline step. After confirmation the
    // refreshed lifecycle decides the next screen; the frontend never forces the cycle state.
    if (cycle.status === "activating_baseline_pending" && cycle.activation_recovery_pending === false) {
      return (
        <>
          {switcher}
          <BaselineScreen user={user} cycleId={cycle.cycle_id} onConfirmed={setLifecycle} />
        </>
      );
    }
    // Active (or ready_to_complete) with a confirmed Baseline -> the existing BRANDED Challenge,
    // cycle-backed. `key` forces a clean remount when switching cycles so no state bleeds across.
    if (cycle.baseline_confirmed &&
        (cycle.status === "active" || cycle.status === "ready_to_complete")) {
      return (
        <>
          {switcher}
          <Challenge
            key={cycle.cycle_id || "cycle"}
            user={user}
            setUser={setUser}
            source={makeCycleSource(cycle.cycle_id)}
            cycleStatus={cycle.status}
            cycle={cycle}
          />
        </>
      );
    }
    // Any other active/transitional state falls through to the branded legacy-backed app.
  }
  return <Challenge user={user} setUser={setUser} source={makeLegacySource({ user, setUser })} />;
}
